import React from 'react';
import * as _ from "lodash";
import { ColDef } from "@ag-grid-community/all-modules";
import { ColDefBuilder } from "./columnBuilder";
import { Dictionary } from "lodash";
import ColumnConfiguration from "./columnConfiguration";
import RenderParameters from "./renderParameters";
import { Form } from "react-bootstrap";
import type { ConditionalKeys } from 'type-fest';
import { AgGridField, AgGridFieldValue } from './utilityTypes';


type ColumnBuilder<Data, Value> = {
    configuration: ColumnConfiguration,
    builder: ColDefBuilder<Data, Value>;
}

export default class ConfigurableColumnBuilder<Data> {

    private builders: Dictionary<ColumnBuilder<Data, unknown>> = {};

    public for<Field extends AgGridField<Data> = "">(id: string, header: string, field?: Field, description?: string, options?: ColDef): ColDefBuilder<Data, AgGridFieldValue<Data, Field>> {
        const builder = new ColDefBuilder<Data, AgGridFieldValue<Data, Field>>({
            colId: id,
            headerName: header,
            field: field ?? undefined,
            headerTooltip: description,
            ...options
        });

        this.builders[id] = {
            configuration: {
                id,
                displayName: header,
                description,
                hide: options?.hide
            },
            builder
        };
        return builder;
    }

    public forExportOnly(id: string, header: string): ColDefBuilder<Data, unknown> {
        const builder = new ColDefBuilder<Data, unknown>({
            colId: id,
            headerName: header,
            hide: true,
            suppressColumnsToolPanel: true,
            suppressFiltersToolPanel: true
        });
        this.builders[id] = {
            configuration: {
                id,
                displayName: header,
                hide: true
            },
            builder
        };
        return builder;
    }

    public buildCheckbox(id: string, header: string, onChange: (data: Data, isChecked: boolean) => void, field: ConditionalKeys<Data, boolean>, description?: string, options?: ColDef): ColDefBuilder<Data, boolean> {

        const builder = new ColDefBuilder<Data, boolean>({
            colId: id,
            headerName: header,
            field,
            headerTooltip: description,
            getQuickFilterText: () => "",
            cellRenderer: (params: RenderParameters<Data, boolean>) => {
                if (params.node.data == null) return null;
                return (
                    <Form.Check name={field} id={field + params.node.id} custom label=""
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => onChange(params.node.data, event.target.checked)}
                        checked={params.node.data[field] as boolean} />
                )
            },
            ...options
        });

        this.builders[id] = {
            configuration: {
                id,
                displayName: header,
                description,
                hide: options?.hide
            },
            builder
        };
        return builder;
    }

    public listColumns(visibleColumnIds: string[]): ColDef[] {
        return visibleColumnIds?.map(id => this.builders[id]?.builder.build())
            .filter(m => m != null);
    }

    public listColumnConfigurations(): ColumnConfiguration[] {
        return _.map(this.builders, m => m.configuration);
    }

    public listVisibleColumnConfigurations(visibleColumnIds: string[]): ColumnConfiguration[] {
        return visibleColumnIds.map(id => this.builders[id]?.configuration)
            .filter(m => m != null);
    }
}